"use client";

import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { createBrowserSupabaseClient } from "@/utils/supabase/client";
import { Session } from "@supabase/supabase-js";

interface UserInfoType {
    id: string | null;
    role: string | null;
    firstname: string | null;
    lastname: string | null;
    address: string | null;
    postal_code: string | null;
    city: string | null;
    country: string | null;
    birthdate: string | null;
    phone: string | null;
    avatar: string | null;
    email: string | null;
    provider: string | null;
    language: string | null;
    is_subscriber: boolean;
    created_at: string | null;
    updated_at: string | null;
}

interface AuthContextType {
    isAuthenticated: boolean;
    userRole: string | null;
    userId: string | null;
    userEmail: string | null;
    refreshAuth: () => Promise<void>;
    userAvatar: string | null;
    isLoading: boolean;
    user: {
        id: string;
        email: string;
        role: string;
        avatar: string | null;
        phone: string;
    } | null;
    userInfo: UserInfoType | null;
}

const AuthContext = createContext<AuthContextType>({
    isAuthenticated: false,
    userRole: null,
    userId: null,
    userEmail: null,
    refreshAuth: async () => { },
    userAvatar: null,
    isLoading: true,
    user: null,
    userInfo: null,
});

export function useAuth() {
    return useContext(AuthContext);
}

export function AuthProvider({ children }: { children: ReactNode }) {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [userRole, setUserRole] = useState<string | null>(null);
    const [userAvatar, setUserAvatar] = useState<string | null>(null);
    const [userId, setUserId] = useState<string | null>(null);
    const [userEmail, setUserEmail] = useState<string | null>(null);
    const [userPhone, setUserPhone] = useState<string | null>(null);
    const [userInfo, setUserInfo] = useState<UserInfoType | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // Stabiliser l'instance de supabase
    const [supabase] = useState(() => createBrowserSupabaseClient());

    async function refreshAuth() {
        console.log("Refreshing auth...");
        try {
            const { data } = await supabase.auth.getSession();
            console.log("Session data from getSession:", data);
            if (data.session) {
                console.log("Session found, updating user with ID:", data.session.user.id);
                await updateUser(data.session);
            } else {
                console.log("No session found");
                clearUserData();
            }
        } catch (error) {
            console.error("Auth refresh error:", error);
            clearUserData();
        }
        console.log("refreshAuth completed");
    }


    function clearUserData() {
        console.log("Clearing user data from state");
        setIsAuthenticated(false);
        setUserId(null);
        setUserEmail(null);
        setUserRole(null);
        setUserAvatar(null);
        setUserPhone(null);
        setUserInfo(null);
        setIsLoading(false);
    }
    function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
        const timeout = new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error("Timeout exceeded")), ms)
        );
        return Promise.race([promise, timeout]);
    }

    async function updateUser(session: Session) {
        console.log("Updating user with session:", session.user.id);
        setIsAuthenticated(true);
        setUserId(session.user.id);
        setUserEmail(session.user.email || session.user.user_metadata?.email || null);
        const response = await withTimeout(
            supabase.from("users").select("*").eq("id", session.user.id).single() as unknown as Promise<any>,
            1000
        );


        console.log("User query response:", response);
        try {
            console.log("Fetching user data for ID:", session.user.id);


            const { data, error } = await supabase
                .from("users")
                .select("*")
                .eq('id', session.user.id)
                .single();

            console.log("User data response:", { data, error });
            if (error) {
                console.error("Error fetching user data:", error);
                setIsLoading(false);
            } else if (data) {
                console.log("User data found:", data);
                setUserInfo(data);
                setUserRole(data.role);
                setUserAvatar(data.avatar);
                setUserPhone(data.phone);
                if (!userEmail && data.email) {
                    setUserEmail(data.email);
                }
                setIsLoading(false);
            } else {
                console.log("No user data found, ending loading state");
                setIsLoading(false);
            }
        } catch (e) {
            console.error("Exception in user data fetch:", e);
            setIsLoading(false);
        }
    }

    useEffect(() => {
        console.log("Auth provider mounted");
        refreshAuth();

        const { data: { subscription } } = supabase.auth.onAuthStateChange(
            async (event, session) => {
                console.log("Auth state changed:", event);
                if (session) {
                    await updateUser(session);
                } else {
                    clearUserData();
                }
            }
        );

        return () => {
            console.log("Auth provider unmounting");
            subscription.unsubscribe();
        };
    }, [supabase]);

    const user = isAuthenticated
        ? {
            id: userId || "",
            email: userEmail || "",
            role: userRole || "",
            avatar: userAvatar,
            phone: userPhone || "",
        }
        : null;

    const contextValue = {
        isAuthenticated,
        userRole,
        userId,
        userEmail,
        refreshAuth,
        userAvatar,
        isLoading,
        user,
        userInfo,
    };

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
}
